/**
 * Pure keyword vs filter-typeahead rules for `UnifiedFilterBar` (`keywordDisplay: 'inline'`).
 * Covered by unit tests — keep behaviour changes here so regressions are caught in CI.
 *
 * Invariant: typeahead draft text used to **navigate** to a filter dimension (e.g. "po" → Posted)
 * is consumed on pick — it must not remain in the input or be committed as keyword search.
 * Committed keyword (`q`) is never cleared by filter picks.
 */

export type UnifiedFilterKeywordMode =
  | { name: 'closed' }
  | { name: 'suggest' }
  | { name: 'picker' }
  | { name: 'inlineText' }

export type ResolveInlineInputValueArgs = {
  mode: UnifiedFilterKeywordMode
  keywordDisplay: 'inline' | 'chip'
  committedText: string
  keywordDraft: string
  inlineEditing: boolean
}

/** Text shown in the combobox input. */
export function resolveInlineInputValue({
  mode,
  keywordDisplay,
  committedText,
  keywordDraft,
  inlineEditing,
}: ResolveInlineInputValueArgs): string {
  if (mode.name === 'inlineText') {
    return keywordDraft
  }
  if (keywordDisplay === 'inline') {
    if (inlineEditing || keywordDraft.length > 0) {
      return keywordDraft
    }
    return committedText
  }
  return keywordDraft
}

export type ResolveFilterTypeaheadQueryArgs = {
  mode: UnifiedFilterKeywordMode
  keywordDraft: string
}

/**
 * Query driving filter suggestions — separate from committed keyword search (`q`).
 * While the dropdown is open, an empty draft means “show all categories”, not “match committed q”.
 */
export function resolveFilterTypeaheadQuery({
  mode,
  keywordDraft,
}: ResolveFilterTypeaheadQueryArgs): string {
  if (mode.name === 'inlineText') {
    return keywordDraft
  }
  if (keywordDraft.length > 0) {
    return keywordDraft
  }
  return ''
}

export type ShouldCollapseInlineInputArgs = {
  keywordDisplay: 'inline' | 'chip'
  hasChips: boolean
  mode: UnifiedFilterKeywordMode
  keywordDraft: string
  inlineEditing: boolean
  committedText: string
  dropdownOpen: boolean
}

/**
 * Collapse the trailing input when chips fill the row — but never hide an active keyword
 * search or a field the user is interacting with.
 */
export function shouldCollapseInlineInput({
  keywordDisplay,
  hasChips,
  mode,
  keywordDraft,
  inlineEditing,
  committedText,
  dropdownOpen,
}: ShouldCollapseInlineInputArgs): boolean {
  if (keywordDisplay !== 'inline') return false
  if (!hasChips) return false
  if (mode.name === 'inlineText') return false
  if (keywordDraft.trim().length > 0) return false
  if (inlineEditing) return false
  if (committedText.length > 0) return false
  if (dropdownOpen) return false
  return true
}

/**
 * True when the in-progress typeahead only matched a filter dimension label (category navigation),
 * not a deliberate value or keyword search.
 */
export function isTypeaheadConsumedByFilterLabel(
  typeaheadDraft: string,
  filterLabel: string,
): boolean {
  const typed = typeaheadDraft.trim()
  if (!typed) return false
  return filterLabel.toLowerCase().includes(typed.toLowerCase())
}

/**
 * Initial inline-text draft after picking an inline-text filter from suggestions.
 * Category-navigation prefixes (e.g. "po" for Posted) are dropped; value intent is kept.
 */
export function resolveInlineTextHandoffDraft(
  typeaheadDraft: string,
  filterLabel: string,
  existingValue: string,
): string {
  if (isTypeaheadConsumedByFilterLabel(typeaheadDraft, filterLabel)) {
    return existingValue
  }
  const typed = typeaheadDraft.trim()
  return typed || existingValue
}

export type ConsumeTypeaheadDraftArgs = {
  typeaheadDraft: string
}

/** Clears navigation-only typeahead after a filter dimension action (picker open, value apply). */
export function consumeTypeaheadDraftForFilterAction(_args: ConsumeTypeaheadDraftArgs): {
  nextDraft: string
  inlineEditing: false
} {
  return { nextDraft: '', inlineEditing: false }
}
